import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { NAV } from "@/config/modules";
import { LogOut, Building2, Smartphone, UsersRound, CalendarClock, HeartHandshake, PillBottle, Inbox, CalendarRange, ShieldAlert, Umbrella, Truck, ClipboardList, CalendarDays, ScrollText, ClipboardCheck, AlertOctagon, ChevronsLeft, ChevronsRight, Star, GripVertical, Database, TrendingUp, BadgePoundSterling, Pill } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PoweredBy } from "@/components/Brand";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** Per-nav-item icon tint when inactive — colour anchors help users scan the sidebar. */
const ICON_TINTS = {
  "nav-dashboard": "text-orange-400",
  "nav-my-shifts": "text-cyan-400",
  "nav-rota": "text-sky-400",
  "nav-calendar": "text-indigo-400",
  "nav-care-plans": "text-emerald-400",
  "nav-risk-register": "text-red-400",
  "nav-staff-schedule": "text-blue-400",
  "nav-mar-chart": "text-pink-400",
  "nav-cd-ledger": "text-rose-400",
  "nav-delivery-notes": "text-teal-400",
  "nav-time-off": "text-amber-400",
  "nav-family-portal": "text-fuchsia-400",
  "nav-family-inbox": "text-violet-400",
  "nav-sites": "text-lime-400",
  "nav-users": "text-purple-400",
  "nav-audit": "text-yellow-400",
  "nav-mobile": "text-cyan-300",
  "nav-training": "text-blue-400",
  "nav-supervisions": "text-cyan-400",
  "nav-medication-competency": "text-pink-300",
  "nav-audits": "text-yellow-400",
  "nav-medication_errors": "text-pink-400",
  "nav-incidents": "text-red-400",
  "nav-complaints": "text-orange-400",
  "nav-outcomes": "text-emerald-400",
  "nav-maintenance": "text-amber-400",
  "nav-clients": "text-fuchsia-400",
  "nav-staff": "text-purple-400",
  "nav-migration": "text-emerald-400",
};
const DEFAULT_TINT = "text-slate-400";
const COLLAPSE_KEY = "ayobell.sidebar.collapsed";
const FAVS_KEY = "ayobell.sidebar.favs";
const MAX_FAVS = 5;

// Extras a Care Worker may pin from the sidebar preferences popover.
// Whitelisted so they cannot bypass the security-critical hidden set — the
// backend still returns 403 on management endpoints regardless.
const CARE_WORKER_EXTRAS = [
  { testid: "nav-care-plans", label: "Care Plans" },
  { testid: "nav-risk-register", label: "Risk Register" },
  { testid: "nav-cd-ledger", label: "CD Ledger" },
  { testid: "nav-delivery-notes", label: "Delivery Notes" },
  { testid: "nav-staff-schedule", label: "Staff Schedule" },
];
const CARE_WORKER_EXTRAS_KEY = "ayobell.sidebar.extras.care_worker";
const CARE_WORKER_EXTRAS_MAX = 2;

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(COLLAPSE_KEY);
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch { /* noop */ }
    return typeof window !== "undefined" && window.innerWidth < 1024;
  });
  const [favs, setFavs] = useState(() => {
    try {
      const raw = localStorage.getItem(FAVS_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return [];
  });
  // Care Worker "pinned extras" (max 2). Client-only preference; the backend
  // still enforces 403 on manager endpoints.
  const [careExtras, setCareExtras] = useState(() => {
    try {
      const raw = localStorage.getItem(CARE_WORKER_EXTRAS_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return [];
  });
  const [extrasOpen, setExtrasOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(CARE_WORKER_EXTRAS_KEY, JSON.stringify(careExtras)); } catch { /* noop */ }
  }, [careExtras]);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* noop */ }
  }, [collapsed]);
  useEffect(() => {
    try { localStorage.setItem(FAVS_KEY, JSON.stringify(favs)); } catch { /* noop */ }
  }, [favs]);

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "manager") return;
    let cancelled = false;
    const load = () => api.get("/portal/unread-count").then((r) => { if (!cancelled) setUnread(r.data?.unread || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user?.role]);

  // Care Workers get a trimmed nav — only the modules they need in day-to-day work.
  // Rota, Sites, Users, Audit Centre, Data Migration, executive analytics etc.
  // are all hidden. The backend still enforces 403 for anything sensitive.
  const CARE_WORKER_ALLOWED_TESTIDS = useMemo(
    () => new Set([
      "nav-dashboard", "nav-my-shifts", "nav-mar-chart",
      "nav-time-off", "nav-calendar", "nav-mobile",
      // Records the care worker legitimately needs to log against a service user:
      "nav-daily_notes", "nav-personal_care", "nav-food_fluid",
      "nav-vital_signs", "nav-medication", "nav-incidents",
      "nav-clients",  // Service Users list (read-scoped by backend)
      "nav-medication-competency",  // Read-only view of own competency history
    ]),
    [],
  );
  const isFrontline = user && user.role === "care_worker";

  // Build the full nav (groups) then also a flat index by testid for the Favourites group.
  const groups = useMemo(() => {
    const g = NAV.reduce((acc, item) => {
      const key = item.group || "Main";
      (acc[key] ||= []).push(item);
      return acc;
    }, {});
    const careDelivery = [
      { path: "/my-shifts", label: "My Shifts", icon: ClipboardList, testid: "nav-my-shifts" },
      { path: "/rota", label: "Rota & Visits", icon: CalendarClock, testid: "nav-rota" },
      { path: "/calendar", label: "Appointments", icon: CalendarDays, testid: "nav-calendar" },
      { path: "/care-plans", label: "Care Plans", icon: ClipboardCheck, testid: "nav-care-plans" },
      { path: "/risk-register", label: "Risk Register", icon: AlertOctagon, testid: "nav-risk-register" },
      { path: "/staff-schedule", label: "Staff Schedule", icon: CalendarRange, testid: "nav-staff-schedule" },
      { path: "/mar-chart", label: "MAR Chart", icon: PillBottle, testid: "nav-mar-chart" },
      { path: "/cd-ledger", label: "CD Ledger", icon: ShieldAlert, testid: "nav-cd-ledger" },
      { path: "/delivery-notes", label: "Delivery Notes", icon: Truck, testid: "nav-delivery-notes" },
      { path: "/time-off-requests", label: "Leave & Holidays", icon: Umbrella, testid: "nav-time-off" },
    ];
    const MANAGER_ROLES = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"];
    if (MANAGER_ROLES.includes(user?.role)) {
      careDelivery.push({ path: "/family-portal", label: "Family Portal", icon: HeartHandshake, testid: "nav-family-portal" });
      careDelivery.push({ path: "/family-inbox", label: "Family Inbox", icon: Inbox, testid: "nav-family-inbox", badge: unread });
    }
    g["Care Delivery"] = [...(g["Care Delivery"] || []), ...careDelivery];

    // ---- People — add Medication Competency next to Supervision & Service Users ----
    // Medication Competency is the ONLY new sidebar item introduced by this
    // sub-module. Supervision (which opens the combined Supervision & Appraisals
    // page) and Service Users remain exactly as they were.
    if (MANAGER_ROLES.includes(user?.role) || user?.role === "care_worker") {
      g["People"] = [
        ...(g["People"] || []),
        { path: "/medication-competency", label: "Medication Competency", icon: Pill, testid: "nav-medication-competency" },
      ];
    }

    // ---- Workforce & Finance (Admin + Manager only) ----------------------
    // Both entries reuse existing routes/components — no duplicate pages.
    //   /staff-schedule      → hosts <StaffSchedule> which mounts <PayrollWidget>
    //   /client-care-cost    → <ClientCareCost>
    // Care worker exclusion is enforced by the whitelist trim below.
    if (MANAGER_ROLES.includes(user?.role)) {
      g["Workforce & Finance"] = [
        { path: "/payroll", label: "Payroll & Staff Hours", icon: CalendarRange, testid: "nav-payroll-staff-hours" },
        { path: "/client-care-cost", label: "Client Care Costs", icon: BadgePoundSterling, testid: "nav-client-care-cost" },
      ];
    }

    const systemItems = [];
    if (user?.role === "admin" || user?.role === "super_admin") {
      systemItems.push({ path: "/sites", label: "Sites", icon: Building2, testid: "nav-sites" });
      systemItems.push({ path: "/users", label: "Staff & Users", icon: UsersRound, testid: "nav-users" });
      systemItems.push({ path: "/migration", label: "Data Migration", icon: Database, testid: "nav-migration" });
      systemItems.push({ path: "/migration/executive", label: "Migrations · Executive", icon: TrendingUp, testid: "nav-migration-exec" });
    }
    if (!isFrontline) {
      systemItems.push({ path: "/audit", label: "Audit Centre", icon: ScrollText, testid: "nav-audit" });
    }
    systemItems.push({ path: "/mobile", label: "Mobile view", icon: Smartphone, testid: "nav-mobile" });
    if (systemItems.length) g["System"] = systemItems;

    // ---- Care Worker trim ----------------------------------------------
    // Keep only whitelisted testids so hiding is enforced at the render layer
    // in addition to the backend 403s the manager endpoints already return.
    if (isFrontline) {
      const allowed = new Set(CARE_WORKER_ALLOWED_TESTIDS);
      // Also allow anything the care worker has pinned as an "extra".
      careExtras.forEach((testid) => allowed.add(testid));
      const trimmed = {};
      for (const [group, items] of Object.entries(g)) {
        const kept = items.filter((it) => allowed.has(it.testid));
        if (kept.length) trimmed[group] = kept;
      }
      return trimmed;
    }
    return g;
  }, [user?.role, unread, isFrontline, CARE_WORKER_ALLOWED_TESTIDS, careExtras]);

  const byTestId = useMemo(() => {
    const m = {};
    Object.values(groups).forEach((items) => items.forEach((it) => { m[it.testid] = it; }));
    return m;
  }, [groups]);

  const favItems = useMemo(
    () => favs.map((tid) => byTestId[tid]).filter(Boolean),
    [favs, byTestId]
  );

  const toggleFav = useCallback((testid) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFavs((current) => {
      if (current.includes(testid)) {
        return current.filter((t) => t !== testid);
      }
      if (current.length >= MAX_FAVS) {
        toast.error(`You can pin up to ${MAX_FAVS} favourites — remove one first`);
        return current;
      }
      const label = byTestId[testid]?.label || "Item";
      toast.success(`Pinned "${label}" to Favourites`);
      return [...current, testid];
    });
  }, [byTestId]);

  const renderItem = (it, opts = {}) => {
    const Icon = it.icon;
    const active = location.pathname === it.path || (it.path !== "/" && location.pathname.startsWith(it.path));
    const tint = ICON_TINTS[it.testid] || DEFAULT_TINT;
    const pinned = favs.includes(it.testid);
    const isInFavList = !!opts.inFavGroup;
    const testIdSuffix = isInFavList ? "-fav" : "";
    const dragHandle = opts.dragHandle;  // {attributes, listeners, setActivatorNodeRef} when sortable
    return (
      <NavLink
        to={it.path}
        data-testid={`${it.testid}${testIdSuffix}`}
        title={it.fullLabel || it.label}
        className={`group relative flex items-center gap-3 py-2 rounded-md text-sm transition-all duration-200 ease-out ${
          collapsed ? "justify-center px-2" : "pl-3 pr-2"
        } ${
          active
            ? `bg-slate-800 text-white font-semibold shadow-[inset_4px_0_0_0_#7CC142] ${collapsed ? "" : "pl-4"}`
            : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
        }`}
      >
        {dragHandle && !collapsed && (
          <button
            ref={dragHandle.setActivatorNodeRef}
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            {...dragHandle.attributes}
            {...dragHandle.listeners}
            data-testid={`drag-${it.testid}-fav`}
            title="Drag to reorder"
            className="shrink-0 p-0.5 -ml-1 rounded cursor-grab active:cursor-grabbing text-slate-500 hover:text-amber-400 opacity-0 group-hover:opacity-100 focus:opacity-100 touch-none"
          >
            <GripVertical className="w-3 h-3" strokeWidth={2} />
          </button>
        )}
        <Icon
          className={`w-[18px] h-[18px] shrink-0 transition-colors duration-200 ${active ? "text-white" : tint}`}
          strokeWidth={active ? 2.25 : 1.9}
        />
        {!collapsed && (
          <>
            <span className="truncate flex-1">{it.label}</span>
            {it.badge > 0 && (
              <span data-testid={`badge-${it.testid}${testIdSuffix}`} className="text-[10px] font-semibold bg-red-500 text-white px-1.5 rounded-full tabular-nums">
                {it.badge > 99 ? "99+" : it.badge}
              </span>
            )}
            <button
              type="button"
              onClick={toggleFav(it.testid)}
              data-testid={`pin-${it.testid}${testIdSuffix}`}
              title={pinned ? "Unpin from Favourites" : "Pin to Favourites"}
              className={`shrink-0 p-1 -mr-1 rounded transition-opacity duration-150 ${
                pinned ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
              }`}
            >
              <Star
                className={`w-3.5 h-3.5 transition-colors ${pinned ? "text-amber-400" : "text-slate-500 hover:text-amber-400"}`}
                fill={pinned ? "currentColor" : "none"}
                strokeWidth={2}
              />
            </button>
          </>
        )}
        {collapsed && it.badge > 0 && (
          <span
            data-testid={`badge-${it.testid}${testIdSuffix}`}
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 shadow-[0_0_0_2px_rgb(15,23,42)]"
            title={`${it.badge} unread`}
          />
        )}
      </NavLink>
    );
  };

  // --- Favourites drag-reorder ---------------------------------------------
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleFavDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFavs((current) => {
      const from = current.indexOf(active.id);
      const to = current.indexOf(over.id);
      if (from < 0 || to < 0) return current;
      return arrayMove(current, from, to);
    });
  };

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={`relative shrink-0 h-full bg-slate-900 text-slate-100 flex flex-col border-r border-slate-800 transition-[width] duration-200 ease-out ${collapsed ? "w-16" : "w-60"}`}
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        data-testid="sidebar-collapse-toggle"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-6 z-20 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 hover:bg-slate-700 hover:border-emerald-500 flex items-center justify-center shadow-md transition-colors duration-200"
      >
        {collapsed
          ? <ChevronsRight className="w-3.5 h-3.5 text-slate-300" />
          : <ChevronsLeft className="w-3.5 h-3.5 text-slate-300" />}
      </button>

      <Link
        to="/"
        className={`pt-5 pb-4 flex flex-col items-center gap-3 border-b border-white/10 hover:opacity-90 transition-opacity ${collapsed ? "px-2" : "px-4"}`}
        data-testid="sidebar-header"
        aria-label="Ayobell home"
      >
        <img
          src="/brand/icon-192.png"
          alt="Ayobell"
          className={`object-contain drop-shadow-[0_4px_18px_rgba(34,197,94,0.25)] transition-all duration-200 ${collapsed ? "w-10 h-10" : "w-[68px] h-[68px]"}`}
          data-testid="sidebar-brand-mark"
        />
        {!collapsed && (
          <div className="text-center min-w-0 w-full">
            <div className="font-heading font-bold text-white text-[21px] leading-none" style={{ letterSpacing: "0.5px" }} data-testid="sidebar-brand-primary">
              AYOBELL
            </div>
            <div className="text-[12px] mt-1.5 font-medium" style={{ color: "rgba(255,255,255,0.70)", letterSpacing: "1px" }} data-testid="sidebar-brand-subtitle">
              Care Management Platform
            </div>
          </div>
        )}
      </Link>

      <nav className={`flex-1 overflow-y-auto overflow-x-hidden pt-5 pb-3 ${collapsed ? "px-1" : ""}`}>
        {favItems.length > 0 && (
          <div className="mb-4" data-testid="sidebar-favourites">
            {!collapsed ? (
              <div className="px-4 pb-1 text-[10px] uppercase tracking-widest text-amber-400 font-semibold flex items-center gap-1">
                <Star className="w-3 h-3" fill="currentColor" strokeWidth={2} /> Favourites
              </div>
            ) : (
              <div className="mx-2 mb-1 flex items-center justify-center text-amber-400" title="Favourites">
                <Star className="w-3 h-3" fill="currentColor" strokeWidth={2} />
              </div>
            )}
            <ul className={`space-y-0.5 ${collapsed ? "px-1" : "px-2"}`}>
              {collapsed ? (
                favItems.map((it) => <li key={it.path + "-fav"}>{renderItem(it, { inFavGroup: true })}</li>)
              ) : (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleFavDragEnd} modifiers={[]}>
                  <SortableContext items={favs} strategy={verticalListSortingStrategy}>
                    {favItems.map((it) => (
                      <SortableFavItem key={it.testid} id={it.testid} item={it} renderItem={renderItem} />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </ul>
            {!collapsed && (
              <div className="mx-4 mt-3 border-b border-slate-800" aria-hidden="true" />
            )}
          </div>
        )}

        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className="mb-4">
            {!collapsed && (
              <div className="px-4 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                {group}
              </div>
            )}
            {collapsed && (
              <div className="mx-2 mb-1 border-b border-slate-800" aria-hidden="true" />
            )}
            <ul className={`space-y-0.5 ${collapsed ? "px-1" : "px-2"}`}>
              {items.map((it) => <li key={it.path}>{renderItem(it)}</li>)}
            </ul>
          </div>
        ))}

        {/* Care Worker "pin extras" preferences popover */}
        {isFrontline && !collapsed && (
          <div className="mx-2 mt-2" data-testid="care-worker-extras">
            <button
              type="button"
              onClick={() => setExtrasOpen((o) => !o)}
              data-testid="btn-pin-extras"
              className="w-full flex items-center justify-between text-[11px] text-slate-400 hover:text-slate-200 border border-dashed border-slate-700 hover:border-slate-500 rounded-md px-2 py-1.5 transition-colors"
            >
              <span className="inline-flex items-center gap-1"><Star className="w-3 h-3" strokeWidth={2} /> Pin extras · {careExtras.length}/{CARE_WORKER_EXTRAS_MAX}</span>
              <span className="text-slate-500">{extrasOpen ? "▲" : "▼"}</span>
            </button>
            {extrasOpen && (
              <div className="mt-1 bg-slate-800 border border-slate-700 rounded-md p-2 space-y-1" data-testid="extras-popover">
                <div className="text-[10px] text-slate-400 mb-1">Pin up to {CARE_WORKER_EXTRAS_MAX} extra menu items:</div>
                {CARE_WORKER_EXTRAS.map((e) => {
                  const checked = careExtras.includes(e.testid);
                  const disabled = !checked && careExtras.length >= CARE_WORKER_EXTRAS_MAX;
                  return (
                    <label key={e.testid} className={`flex items-center gap-2 text-xs px-1.5 py-1 rounded cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-700"}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={(ev) => {
                          setCareExtras((cur) => {
                            if (ev.target.checked) {
                              if (cur.length >= CARE_WORKER_EXTRAS_MAX) return cur;
                              return [...cur, e.testid];
                            }
                            return cur.filter((t) => t !== e.testid);
                          });
                        }}
                        data-testid={`extra-${e.testid}`}
                      />
                      <span className="text-slate-200">{e.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className={`border-t border-slate-800 ${collapsed ? "p-2" : "p-3"}`}>
        {collapsed ? (
          <>
            <div className="w-9 h-9 mx-auto rounded-full bg-gradient-to-br from-slate-700 to-slate-950 flex items-center justify-center text-xs font-semibold mb-2 border border-slate-700" title={`${user?.name || user?.email} · ${user?.role || ""}`}>
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <button
              data-testid="btn-logout" onClick={logout} title="Sign out"
              className="w-full h-9 flex items-center justify-center rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-sky-600 flex items-center justify-center text-xs font-semibold">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">{user?.role?.replace("_", " ")}</div>
              </div>
            </div>
            <button
              data-testid="btn-logout" onClick={logout}
              className="w-full flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-sm bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
            <div className="mt-3 pt-3 border-t border-slate-800 text-center">
              <PoweredBy theme="dark" />
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

// Sortable wrapper for a favourite item — provides the drag handle to renderItem.
function SortableFavItem({ id, item, renderItem }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : "auto",
    position: "relative",
  };
  return (
    <li ref={setNodeRef} style={style} data-testid={`fav-sortable-${id}`}>
      {renderItem(item, { inFavGroup: true, dragHandle: { attributes, listeners, setActivatorNodeRef } })}
    </li>
  );
}
